import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect, useRef } from "react";
import { useApi } from "@/core/api";
import { pollStatus, type PollingRuntimeOptions } from "@/core/api/polling";
import type {
  CompletedPayrollRosterTechnicianSync,
  PayrollRoster,
  PayrollRosterTechnicianSync,
  SyncPayrollRosterTechniciansResponse,
} from "@/payroll/roster/types";
import { replacePayrollRosterSuggestions } from "./cache-entries";
import { payrollRosterKey } from "./keys";
import {
  payrollRosterSyncTechniciansPath,
  payrollRosterTechnicianSyncStatusPath,
} from "./paths";
import { useRosterViewer } from "./use-roster-viewer";

export const pollPayrollRosterTechnicianSync = async ({
  initial,
  request,
  signal,
  now,
  wait,
}: PollingRuntimeOptions & {
  initial: PayrollRosterTechnicianSync;
  request: (signal: AbortSignal) => Promise<PayrollRosterTechnicianSync>;
  signal: AbortSignal;
}): Promise<CompletedPayrollRosterTechnicianSync> => {
  const result = await pollStatus({
    initial,
    isPending: (sync) => sync.status === "queued" || sync.status === "running",
    request,
    signal,
    now,
    wait,
  });

  if (result.polling_timed_out) {
    throw new Error("Technician sync timed out. Try again.");
  }
  if (result.status === "failed") {
    throw new Error(result.error ?? "Technician sync failed");
  }
  if (result.status !== "completed") {
    throw new Error("Technician sync ended with an unknown status");
  }

  return result;
};

export const syncPayrollRosterTechnicians = async (
  api: AxiosInstance,
  businessId: string,
  options: PollingRuntimeOptions & {
    signal?: AbortSignal;
  } = {},
) => {
  const signal = options.signal ?? new AbortController().signal;
  const response = await api.post<SyncPayrollRosterTechniciansResponse>(
    payrollRosterSyncTechniciansPath(businessId),
    undefined,
    { signal },
  );
  const initial = response.data.technician_sync;

  return pollPayrollRosterTechnicianSync({
    initial,
    signal,
    now: options.now,
    wait: options.wait,
    request: (requestSignal) =>
      api
        .get<SyncPayrollRosterTechniciansResponse>(
          payrollRosterTechnicianSyncStatusPath(businessId, initial.id),
          { signal: requestSignal },
        )
        .then((pollResponse) => pollResponse.data.technician_sync),
  });
};

export const useSyncPayrollRosterTechnicians = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("Business is required");

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        return await syncPayrollRosterTechnicians(api, businessId, {
          signal: controller.signal,
        });
      } catch (error) {
        if (controller.signal.aborted) {
          const canceled = new Error("Technician sync canceled");
          canceled.name = "AbortError";
          throw canceled;
        }
        throw error;
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    onSuccess: (synced) => {
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        (current) => replacePayrollRosterSuggestions(current, synced),
      );
    },
  });
};
